import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { dismissToast, type ToastTone } from '@/store/slices/uiSlice';
import { cn } from '@/lib/utils';

const TONE_STYLES: Record<ToastTone, { icon: typeof Info; className: string }> = {
  info: { icon: Info, className: 'text-info' },
  success: { icon: CheckCircle2, className: 'text-positive' },
  warning: { icon: AlertTriangle, className: 'text-warning' },
  danger: { icon: XCircle, className: 'text-danger' },
};

/**
 * Toasts live in Redux so any thunk or page can raise one without prop drilling or a
 * separate context.
 */
export function Toaster() {
  const toasts = useAppSelector((state) => state.ui.toasts);
  const dispatch = useAppDispatch();

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
      {toasts.map((toast) => {
        const { icon: Icon, className } = TONE_STYLES[toast.tone];

        return (
          <ToastPrimitive.Root
            key={toast.id}
            onOpenChange={(open) => !open && dispatch(dismissToast(toast.id))}
            className={cn(
              'flex items-start gap-3 rounded-[var(--radius-card)] border border-line bg-overlay p-4 shadow-pop',
              'data-[state=open]:animate-[var(--animate-slide-up)]',
              'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=end]:opacity-0',
              'data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform'
            )}
          >
            <Icon className={cn('mt-0.5 size-4.5 shrink-0', className)} aria-hidden />
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title className="text-[13.5px] font-semibold text-ink">
                {toast.title}
              </ToastPrimitive.Title>
              {toast.description && (
                <ToastPrimitive.Description className="mt-0.5 text-[12.5px] leading-relaxed text-ink-muted">
                  {toast.description}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close
              className="rounded-md p-1 text-ink-subtle transition-colors hover:bg-surface-raised hover:text-ink"
              aria-label="Dismiss"
            >
              <X className="size-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        );
      })}

      <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-100 flex max-h-dvh w-full max-w-96 flex-col gap-2 p-4 outline-none" />
    </ToastPrimitive.Provider>
  );
}
